import { useState } from "react";
import type { ReactElement } from "react";
import { ballLabel, extraLabel, isWicketBall, oversText, personName } from "./derive";
import type { OverVm } from "./derive";
import type { ICommentaryBall } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { Crest, Empty, Seg } from "./shared";
import { innOpts } from "./innOpts";
import type { Ctx } from "./shared";

const SHOW_FIRST = 4;

const chipFor = (b: ICommentaryBall): { cls: string; text: string; row: string } => {
  const ex = extraLabel(b);
  if (isWicketBall(b)) return { cls: "w", text: "W", row: "w" };
  if (ex) return { cls: "x", text: ex, row: "" };
  const r = b.runs_scored ?? 0;
  if (r === 4) return { cls: "f", text: "4", row: "f" };
  if (r === 6) return { cls: "s", text: "6", row: "s" };
  if (r === 0) return { cls: "d", text: "•", row: "" };
  return { cls: "", text: String(r), row: "" };
};

const OverEnd = ({ o, teamName }: { o: OverVm; teamName: string }) => {
  const r = o.raw;
  const bats = [r.firstBatsman, r.secondBatsman].filter((p) => p && personName(p.firstName, p.lastName));
  const bw = r.bowler;
  return (
    <li className="oend">
      <div className="h">
        End of over {o.n}
        <span style={{ fontWeight: 600, color: "var(--muted)" }}>
          {o.runs} run{o.runs === 1 ? "" : "s"}{o.wkts ? ` · ${o.wkts} wicket${o.wkts > 1 ? "s" : ""}` : ""}
        </span>
        <span className="sc">{teamName} {o.cumRuns}/{o.cumWkts}</span>
      </div>
      <div className="d">
        {bats.map((p) => (
          <span key={`${p.firstName}${p.lastName}`}><b>{personName(p.firstName, p.lastName)}</b> {p.runs ?? 0}* ({p.balls ?? 0})</span>
        ))}
        {bw && personName(bw.firstName, bw.lastName) && (
          <span>{personName(bw.firstName, bw.lastName)} <b>{bw.overs}-{bw.maidens}-{bw.runs}-{bw.wickets}</b></span>
        )}
      </div>
    </li>
  );
};

const CommentaryPane = ({ ctx }: { ctx: Ctx }) => {
  const { inns, fullComms: comms, fullLoading: commLoading } = ctx;
  const [cur, setCur] = useState(String(Math.max(inns.length - 1, 0)));
  const [filter, setFilter] = useState("all");
  const [all, setAll] = useState(false);
  const idx = Math.min(Number(cur), Math.max(inns.length - 1, 0));
  const inn = inns[idx];
  const comm = comms[idx];

  if (!inn) {
    return (
      <section className="pane on" id="pane-commentary" role="tabpanel" aria-labelledby="tab-commentary">
        <div className="box"><Empty>No commentary is available for this match.</Empty></div>
      </section>
    );
  }

  const overs = comm ? [...comm.overs].reverse() : [];
  const shown = !all && filter === "all" ? overs.slice(0, SHOW_FIRST) : overs;
  const rows: ReactElement[] = [];
  shown.forEach((o) => {
    if (filter === "all") rows.push(<OverEnd key={`e${o.api}`} o={o} teamName={inn.team.name} />);
    [...o.balls].reverse().forEach((b, i) => {
      if (filter === "w" && !isWicketBall(b)) return;
      if (filter === "b" && !(b.runs_scored === 4 || b.runs_scored === 6 || b.boundary)) return;
      const chip = chipFor(b);
      const who = `${personName(b.bowler?.playerFirstName, b.bowler?.playerLastName)} to ${personName(b.batsman?.playerFirstName, b.batsman?.playerLastName)}`;
      rows.push(
        <li key={`${o.api}-${b.ball_id}-${i}`} className={chip.row}>
          <span className="lab">{ballLabel(o, b)}</span>
          <span className={`bl ${chip.cls}`}>{chip.text}</span>
          <span className="tx">
            <span className="who">{who}</span>
            {b.commentary ? <>, {b.commentary}</> : null}
            {isWicketBall(b) && b.postWicketCommentary ? <> {b.postWicketCommentary}</> : null}
          </span>
        </li>,
      );
    });
  });

  return (
    <section className="pane on" id="pane-commentary" role="tabpanel" aria-labelledby="tab-commentary">
      <div className="toolbar">
        <Seg label="Innings" opts={innOpts(inns)} cur={String(idx)} onPick={(k) => { setCur(k); setAll(false); }} />
        <div className="r">
          <Seg label="Filter" opts={[{ key: "all", label: "All balls" }, { key: "w", label: "Wickets" }, { key: "b", label: "Fours & sixes" }]} cur={filter} onPick={setFilter} />
        </div>
      </div>
      <div className="box">
        <ul className="cm">
          <li className="ibr">
            <Crest team={inn.team} className="chip-c" />
            {["1st", "2nd", "3rd", "4th"][idx] ?? `${idx + 1}th`} innings {"·"} {inn.team.name}{idx === 1 && inns.length === 2 && !ctx.multiDay ? `, chasing ${inns[0].runs + 1}` : ""}
            <span className="n">{inn.runs}/{inn.wkts} ({oversText(inn.legal)})</span>
          </li>
          {commLoading && !comm ? <li className="empty">Loading commentary...</li> : rows.length ? rows : <li className="empty">{comm ? "No balls match this filter." : "Commentary is not available for this innings."}</li>}
        </ul>
        {comm && filter === "all" && overs.length > SHOW_FIRST ? (
          <div className="box-f">
            <span>Ball by ball, newest first. Showing {all ? overs.length : SHOW_FIRST} of {overs.length} overs.</span>
            <button type="button" className="link" style={{ marginLeft: "auto" }} onClick={() => setAll(!all)}>{all ? "Show fewer" : "Show all overs"}</button>
          </div>
        ) : comm ? (
          <div className="box-f"><span>Ball by ball, newest first.</span></div>
        ) : null}
      </div>
    </section>
  );
};

export default CommentaryPane;
